import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramBETEffectiveLeakageArea } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.betEffectiveLeakageArea.v1';

/**
 * Effective Leakage Area (ELA) — sharp-edged orifice approximation.
 *   A(ft²) = Q(cfm) / (C_d × 1096 × √(ΔP[in. w.c.]))
 * Bidirectional: fill any 3 of {Q, ΔP, C_d, A} to solve the 4th.
 *
 * ELA is displayed in in² (Imperial) or cm² (SI).
 */
const ORIFICE_CONSTANT = 1096;
const IN2_TO_CM2 = 6.4516;

type Fields = { airflow: string; pressure: string; cd: string; elaIn2: string };
const EMPTY: Fields = { airflow: '', pressure: '', cd: '0.61', elaIn2: '' };

const BETEffectiveLeakageAreaScreen = () => {
  const { isImperial, unit, toImp, fromImp } = useUnits();
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  // Convert ELA display → in² (Imperial base)
  const elaToIn2 = (s: string) => {
    const n = parseFloat(s);
    if (!Number.isFinite(n)) return NaN;
    return isImperial ? n : n / IN2_TO_CM2;
  };
  const elaFromIn2 = (in2: number, dec = 2) => {
    if (!Number.isFinite(in2)) return '';
    return parseFloat((isImperial ? in2 : in2 * IN2_TO_CM2).toFixed(dec)).toString();
  };
  const elaUnit = isImperial ? 'in²' : 'cm²';

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 3) { setError('Fill any 3 of {Flow, ΔP, C_d, Area} to solve the 4th.'); return; }
    if (filled === 4) { setError('All 4 values are filled — clear one to solve for it.'); return; }

    const q = toImp(fields.airflow, 'flow');                    // CFM
    const dp = toImp(fields.pressure, 'pressure');              // in. w.c.
    const cd = parseFloat(fields.cd);
    const aIn2 = elaToIn2(fields.elaIn2);
    const aFt2 = aIn2 / 144;

    if (fields.cd !== '' && (cd <= 0 || cd > 1.2)) {
      setError('Discharge coefficient should be in (0, 1.2] (typical 0.6–1.0).');
      return;
    }

    if (fields.elaIn2 === '') {
      if (!(q > 0) || !(dp > 0) || !(cd > 0)) { setError('Flow, ΔP, and C_d must be > 0.'); return; }
      const ft2 = q / (cd * ORIFICE_CONSTANT * Math.sqrt(dp));
      setSolvedFor('elaIn2');
      set({ elaIn2: elaFromIn2(ft2 * 144, 2) });
    } else if (fields.airflow === '') {
      if (!(dp > 0) || !(cd > 0) || !(aFt2 > 0)) { setError('ΔP, C_d, and Area must be > 0.'); return; }
      const qSolved = aFt2 * cd * ORIFICE_CONSTANT * Math.sqrt(dp);
      setSolvedFor('airflow');
      set({ airflow: fromImp(qSolved, 'flow', 2) });
    } else if (fields.pressure === '') {
      if (!(q > 0) || !(cd > 0) || !(aFt2 > 0)) { setError('Flow, C_d, and Area must be > 0.'); return; }
      const dpSolved = Math.pow(q / (aFt2 * cd * ORIFICE_CONSTANT), 2);
      setSolvedFor('pressure');
      set({ pressure: fromImp(dpSolved, 'pressure', 4) });
    } else if (fields.cd === '') {
      if (!(q > 0) || !(dp > 0) || !(aFt2 > 0)) { setError('Flow, ΔP, and Area must be > 0.'); return; }
      const cdSolved = q / (aFt2 * ORIFICE_CONSTANT * Math.sqrt(dp));
      if (cdSolved <= 0 || cdSolved > 1.2) { setError(`Computed C_d=${cdSolved.toFixed(3)} outside plausible range.`); return; }
      setSolvedFor('cd');
      set({ cd: cdSolved.toFixed(3) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.airflow && { label: 'Airflow', value: fields.airflow, unit: unit('flow'), emphasis: solvedFor === 'airflow' },
    fields.pressure && { label: 'ΔP', value: fields.pressure, unit: unit('pressure'), emphasis: solvedFor === 'pressure' },
    fields.cd && { label: 'C_d', value: fields.cd, emphasis: solvedFor === 'cd' },
    fields.elaIn2 && { label: 'Effective Leakage Area', value: fields.elaIn2, unit: elaUnit, emphasis: solvedFor === 'elaIn2' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Effective Leakage Area"
      subtitle="Solve for ELA, flow, ΔP, or C_d using the orifice approximation — fill any 3 of 4"
      formula={
        <>
          <FormulaLine>A = Q / (C_d × 1096 × √ΔP)</FormulaLine>
          <FormulaNote>Q in CFM, ΔP in in. w.c., A in ft² (shown in {elaUnit}). C_d ≈ 0.61 for sharp-edged orifice.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: A = Q / (C_d·1096·√ΔP)"
      diagram={<DiagramBETEffectiveLeakageArea />}
      outputSlots={{
        cfm: fields.airflow ? toImp(fields.airflow, 'flow') : undefined,
        pressure_differential_iwg: fields.pressure ? toImp(fields.pressure, 'pressure') : undefined,
        pressure_differential_pa: fields.pressure ? toImp(fields.pressure, 'pressure') * 249.0889 : undefined,
        effective_leakage_area_sqin: fields.elaIn2 ? elaToIn2(fields.elaIn2) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.airflow) patch.airflow = fromImp(values.cfm, 'flow', 2);
        if (values.pressure_differential_iwg != null && !fields.pressure) {
          patch.pressure = fromImp(values.pressure_differential_iwg, 'pressure', 4);
        } else if (values.pressure_differential_pa != null && !fields.pressure) {
          patch.pressure = fromImp(values.pressure_differential_pa / 249.0889, 'pressure', 4);
        }
        if (values.effective_leakage_area_sqin != null && !fields.elaIn2) patch.elaIn2 = elaFromIn2(values.effective_leakage_area_sqin, 2);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Airflow Rate (${unit('flow')})`} value={fields.airflow} onChangeText={(v) => set({ airflow: v })} rightText={unit('flow')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Pressure Differential (${unit('pressure')})`} value={fields.pressure} onChangeText={(v) => set({ pressure: v })} rightText={unit('pressure')} maxDecimals={4} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Discharge Coefficient (C_d)" value={fields.cd} onChangeText={(v) => set({ cd: v })} maxDecimals={3} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Effective Leakage Area (${elaUnit})`} value={fields.elaIn2} onChangeText={(v) => set({ elaIn2: v })} rightText={elaUnit} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default BETEffectiveLeakageAreaScreen;
